import {
  projectsCollection, boardsCollection, tasksCollection, commentsCollection,
} from '@/firebase';
import moment from 'moment';
import DateParser from '@/parsers/date';
import UsersService from './users';

export default class ProjectsService {
  static async fetchCompanyProjects(companyId) {
    const projects = [];
    const snapshot = await projectsCollection
      .where('company_id', '==', companyId)
      .get();

    snapshot.forEach((doc) => projects.push({
      uid: doc.id,
      ...doc.data(),
    }));

    return projects;
  }

  static async fetchProjectBySlug({ companyId, slug }) {
    let project;
    const snapshot = await projectsCollection
      .where('company_id', '==', companyId)
      .where('slug', '==', slug)
      .get();

    snapshot.forEach((doc) => {
      project = {
        uid: doc.id,
        ...doc.data(),
      };
    });

    return project;
  }

  static async updateProjectById(projectId, data) {
    return projectsCollection.doc(projectId).set(data, { merge: true });
  }

  static async fetchDefaultProjectInfo({ companyId }) {
    let project;
    const snapshot = await projectsCollection
      .where('company_id', '==', companyId)
      .get();

    snapshot.forEach((doc) => {
      const data = doc.data();

      if (data.default) {
        project = {
          uid: doc.id,
          ...doc.data(),
        };
      }
    });

    return project;
  }

  static async fetchProjectBoards(projectId) {
    const boards = [];
    const snapshot = await boardsCollection
      .where('project_id', '==', projectId)
      .get();

    snapshot.forEach((doc) => {
      boards.push({
        uid: doc.id,
        ...doc.data(),
      });
    });

    return boards.sort((current, next) => (current.order > next.order ? 1 : -1));
  }

  static async updateBoardById(boardId, data) {
    return boardsCollection.doc(boardId).set(data, { merge: true });
  }

  static async fetchProjectTasks({
    projectId, userId, filterByUser, brokerId,
  }) {
    const tasks = [];
    const snapshot = await tasksCollection
      .where('project_id', '==', projectId)
      .orderBy('created_at', 'asc')
      .get();

    function getTaskSituation(date) {
      if (!date) {
        return 'unknown';
      }

      const formatted = DateParser.parseFromTimestamp(date.seconds);

      if (DateParser.isBeforeToday(formatted)) {
        return 'past';
      }

      if (DateParser.isAfterToday(formatted)) {
        return 'future';
      }

      return 'today';
    }

    snapshot.forEach((doc) => {
      const data = doc.data();

      tasks.push({
        ...data,
        uid: doc.id,
        created_at: DateParser.parseFromTimestamp(data.created_at.seconds),
        situation: getTaskSituation(data.due_date),
        ...(data.due_date)
          ? { due_date: DateParser.parseFromTimestamp(data.due_date.seconds) }
          : {},
      });
    });

    return Promise.all(
      tasks
      // .filter((item) => {
      //   if (!filterByUser || !item.due_date) {
      //     return true;
      //   }

      //   return item.assignee_id === userId
      //     && DateParser.isTodayOrBefore(item.due_date);
      // })
        .filter((item) => {
          if (filterByUser) {
            return item.assignee_id === userId;
          }

          if (brokerId) {
            return item.assignee_id === brokerId;
          }

          return true;
        })
        .map(
          async (task) => {
            const user = await UsersService.fetchUserProfile(task.assignee_id);

            return {
              ...task,
              assignee: `${user.first_name} ${user.last_name}`,
            };
          },
        ),
    );
  }

  static async fetchDefaultProjectTasks(companyId) {
    const projects = await ProjectsService.fetchCompanyProjects(companyId);
    const project = projects.filter((item) => item.default).pop();

    const tasks = await ProjectsService.fetchProjectTasks({ projectId: project.uid });

    return {
      project,
      tasks,
    };
  }

  static async createDefaultProjectTask(task) {
    const seconds = moment().unix();

    const projects = await ProjectsService.fetchCompanyProjects(task.company_id);
    const projectId = projects.filter((item) => item.default).pop().uid;

    const boards = await ProjectsService.fetchProjectBoards(projectId);
    const boardId = boards[0].uid;

    return tasksCollection.doc().set({
      content: '',
      categories: [],
      archived_at: null,
      board_id: boardId,
      due_date: null,
      is_archived: false,
      project_id: projectId,
      created_at: { seconds },
      updated_at: { seconds },
      ...task,
    });
  }

  static async fetchTaskComments(taskId) {
    const comments = [];
    const snapshot = await commentsCollection
      .where('task_id', '==', taskId)
      .orderBy('created_at')
      .get();

    snapshot.forEach((doc) => {
      const data = doc.data();

      comments.unshift({
        ...data,
        uid: doc.id,
        created_at: DateParser.parseFromTimestamp(data.created_at.seconds),
      });
    });

    return Promise.all(
      comments.map(
        async (comment) => {
          const user = await UsersService.fetchUserProfile(comment.user_id);

          return {
            ...comment,
            author: `${user.first_name} ${user.last_name}`,
          };
        },
      ),
    );
  }

  static async updateTaskDetails(task) {
    return tasksCollection
      .doc(task.id)
      .set({ ...task }, { merge: true });
  }

  static async deleteTaskByClientId(clientId) {
    const snapshot = await tasksCollection
      .where('client_id', '==', clientId)
      .get();

    return snapshot.forEach((doc) => doc.ref.delete());
  }

  static async createComment(comment) {
    return commentsCollection.doc().set(comment);
  }

  static async updateComment(comment) {
    console.log(comment);
  }

  static async createProject(data) {
    return projectsCollection.doc().set(data);
  }

  static async updateProjectInfo(teamId, data) {
    const seconds = moment().unix();

    return projectsCollection
      .doc(teamId)
      .update({
        ...data,
        updated_at: { seconds },
      });
  }

  static async addProjectMember(teamId, data) {
    return projectsCollection.doc(teamId).set(data);
  }

  static async removeProjectMember() {
    return true;
  }

  static async addProjectTeam() {
    return true;
  }

  static async removeProjectTeam() {
    return true;
  }
}
